import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ThemePicker } from '@/components/ThemePicker'
// Removed Select import - using native dropdown to avoid scroll lock
import apiClient from '@/services/api'
import { useAppStore } from '@/store'
import { useAuthStore } from '@/store/auth'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'

export function SettingsPage() {
  const { t, i18n } = useTranslation()
  const { language, setLanguage } = useAppStore()
  const { user, refreshUser, logout } = useAuthStore()
  const navigate = useNavigate()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)

  useEffect(() => {
    if (user) {
      // Get the first and last name directly from the backend response via auth store
      // We need to call the validate token again to get the fresh user data with firstName/lastName
      // For now, let's extract from response or leave empty if displayName is just email
      const isEmailDisplayName = user.displayName.includes('@')
      if (isEmailDisplayName) {
        // If displayName is email, that means firstName/lastName are empty
        setFirstName('')
        setLastName('')
      } else {
        // Parse displayName that was constructed from firstName + lastName
        const names = user.displayName.split(' ')
        setFirstName(names[0] || '')
        setLastName(names.slice(1).join(' ') || '')
      }
    }
  }, [user])

  const handleLanguageChange = async (newLanguage: string) => {
    try {
      setLanguage(newLanguage)
      await i18n.changeLanguage(newLanguage)
      toast.success(t('common.success'))
    } catch (_error) {
      toast.error(t('settings.language.failed'))
    }
  }

  const handleProfileSave = async () => {
    if (!user || !firstName.trim() || !lastName.trim()) {
      toast.error(t('settings.profile.validation.required'))
      return
    }

    setIsUpdatingProfile(true)
    try {
      await apiClient.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim()
      })
      
      // Refresh user data to get updated display name
      await refreshUser()
      
      toast.success(t('settings.profile.updated'))
    } catch (_error) {
      toast.error(t('settings.profile.update.failed'))
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!user || isDeletingAccount) {return}

    setIsDeletingAccount(true)
    try {
      await apiClient.deletePlayer(user.playerId)
      logout()
      toast.success(t('settings.account.deleted'))
      navigate('/login')
    } catch (_error) {
      toast.error(t('settings.account.deleteFailed'))
    } finally {
      setIsDeletingAccount(false)
      setShowDeleteDialog(false)
    }
  }

  const isProfileChanged = user && (
    firstName.trim() !== (user.displayName.split(' ')[0] || '') ||
    lastName.trim() !== (user.displayName.split(' ').slice(1).join(' ') || '')
  )

  const isProfileComplete = firstName.trim() && lastName.trim()
  const isProfileRequired = user && (!user.displayName || user.displayName === user.email)

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">{t('settings.title')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('settings.actor.description')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Language Settings */}
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.language')}</CardTitle>
            <CardDescription>
              {t('settings.language.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="language-select">{t('settings.language')}</Label>
              <select
                id="language-select"
                value={language || 'sv'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="flex h-9 w-full max-w-xs items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="sv">{t('settings.language.sv')}</option>
                <option value="en">{t('settings.language.en')}</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Theme Settings */}
        <ThemePicker />

        {/* Profile Settings */}
        <Card className={isProfileRequired ? "border-destructive/30" : ""}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {t('settings.profile')}
              {isProfileRequired && (
                <span className="text-destructive text-sm font-normal">
                  ({t('settings.profile.required')})
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {t('settings.profile.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first-name-input" className="flex items-center gap-1">
                    {t('settings.profile.firstName')}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="first-name-input"
                    type="text"
                    placeholder={t('settings.profile.firstName.placeholder')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`max-w-md ${!firstName.trim() && isProfileRequired ? 'border-destructive focus:border-destructive' : ''}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name-input" className="flex items-center gap-1">
                    {t('settings.profile.lastName')}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="last-name-input"
                    type="text"
                    placeholder={t('settings.profile.lastName.placeholder')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={`max-w-md ${!lastName.trim() && isProfileRequired ? 'border-destructive focus:border-destructive' : ''}`}
                  />
                </div>
              </div>
              {isProfileRequired && !isProfileComplete && (
                <div className="text-destructive text-sm flex items-center gap-2">
                  <span className="font-medium">⚠️</span>
                  {t('settings.profile.completion.required')}
                </div>
              )}
              <Button
                onClick={handleProfileSave}
                disabled={!isProfileChanged || isUpdatingProfile || !isProfileComplete}
                className="w-fit"
              >
                {isUpdatingProfile ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Account deletion */}
        <Card className="border-destructive/20">
          <CardHeader>
            <CardTitle className="text-destructive">
              {t('settings.account.title')}
            </CardTitle>
            <CardDescription>
              {t('settings.account.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog
              open={showDeleteDialog}
              onOpenChange={(open) => {
                if (!isDeletingAccount) {
                  setShowDeleteDialog(open)
                }
              }}
            >
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                >
                  {t('settings.account.deleteButton')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('settings.account.confirmTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('settings.account.confirmDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeletingAccount}>
                    {t('common.cancel')}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20"
                    onClick={handleDeleteAccount}
                    disabled={isDeletingAccount}
                  >
                    {isDeletingAccount ? t('settings.account.deleting') : t('settings.account.confirmAction')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}